import * as React from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, TextField, Typography } from '@mui/material';
import { getOneClickBetSettings, updateOneClickBetSettings } from '../Api';
import OneClickBetSettingsDto from '../models/dtos/OneClickBetSettingsDto';

function parseLocaleInt(raw: string): number {
    const t = raw.trim().replace(/\s/g, '');
    if (t.includes(',') || t.includes('.')) return Number.NaN;
    return parseInt(t, 10);
}

export default function OneClickBetSettingsPage() {
    const [loading, setLoading] = React.useState(true);
    const [minStake, setMinStake] = React.useState<string>('100');
    const [maxStake, setMaxStake] = React.useState<string>('500000');
    const [unlockCost, setUnlockCost] = React.useState<string>('1000');
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [dialogTitle, setDialogTitle] = React.useState('');
    const [dialogMsg, setDialogMsg] = React.useState('');

    const applyDto = (dto: OneClickBetSettingsDto | null) => {
        if (dto != null) {
            setMinStake(String(dto.minStakeBetcoins ?? 100));
            setMaxStake(String(dto.maxStakeBetcoins ?? 500000));
            setUnlockCost(String(dto.unlockCostBetcoins ?? 1000));
        }
    };

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const dto = await getOneClickBetSettings();
            if (!cancelled) {
                applyDto(dto);
                setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const openDialog = (title: string, message: string) => {
        setDialogTitle(title);
        setDialogMsg(message);
        setDialogOpen(true);
    };

    const onSave = async () => {
        const minS = parseLocaleInt(minStake);
        const maxS = parseLocaleInt(maxStake);
        const unlock = parseLocaleInt(unlockCost);
        if (Number.isNaN(minS) || Number.isNaN(maxS) || Number.isNaN(unlock)) {
            openDialog('Error', 'Enter whole numbers for all fields.');
            return;
        }
        if (minS < 0 || maxS < 0 || unlock < 0) {
            openDialog('Error', 'Values must be non-negative.');
            return;
        }
        if (minS > maxS) {
            openDialog('Error', 'Minimum stake cannot exceed maximum stake.');
            return;
        }
        const payload: OneClickBetSettingsDto = {
            minStakeBetcoins: minS,
            maxStakeBetcoins: maxS,
            unlockCostBetcoins: unlock,
        };
        const ok = await updateOneClickBetSettings(payload);
        if (!ok) {
            openDialog('Error', 'Failed to save. Check server logs and API configuration.');
            return;
        }
        const dto = await getOneClickBetSettings();
        applyDto(dto);
        openDialog('Saved', 'One-click / quick bet settings were saved to the main API (qbet_app_settings).');
    };

    return (
        <Stack gap={3} sx={{ mx: 5, my: 5 }}>
            <Typography variant="h4">One-click / quick bet (Android)</Typography>
            <Typography variant="body2" color="text.secondary">
                Minimum and maximum betcoins the user can set as stake for quick bet. The in-app slider is capped by the
                user balance and by the platform max bet limit as well. Unlock cost is charged once when the user buys the
                feature (POST /api/v7/me/unlock-one-click-bet). Keys: one_click_bet.min_stake_betcoins,
                one_click_bet.max_stake_betcoins, one_click_bet.unlock_cost_betcoins.
            </Typography>
            {loading ? (
                <Box><CircularProgress /></Box>
            ) : (
                <Grid container spacing={2} maxWidth="sm">
                    <Grid item xs={12}>
                        <TextField
                            label="Minimum quick-bet stake (betcoins)"
                            type="number"
                            fullWidth
                            value={minStake}
                            onChange={(e) => setMinStake(e.target.value)}
                            inputProps={{ min: 0 }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Maximum quick-bet stake cap (betcoins)"
                            type="number"
                            fullWidth
                            value={maxStake}
                            onChange={(e) => setMaxStake(e.target.value)}
                            inputProps={{ min: 0 }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Unlock feature — cost (betcoins, one-time)"
                            type="number"
                            fullWidth
                            value={unlockCost}
                            onChange={(e) => setUnlockCost(e.target.value)}
                            inputProps={{ min: 0 }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button variant="contained" onClick={onSave}>Save</Button>
                    </Grid>
                </Grid>
            )}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" component="div">{dialogMsg}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
